import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

const FIELDS = [
  { k: "name", label: "Business name", ph: "e.g. Glow Theory", req: true },
  { k: "industry", label: "Industry", ph: "e.g. D2C skincare", req: true },
  { k: "country", label: "Country / region", ph: "e.g. India", req: true },
  { k: "business_model", label: "Business model", ph: "e.g. Direct-to-consumer" },
  { k: "company_size", label: "Company size", ph: "e.g. Small (18 staff)" },
  { k: "stage", label: "Business stage", ph: "e.g. Early growth" },
  { k: "products_services", label: "Products / services", ph: "e.g. Vitamin-C serum, moisturizers", area: true },
  { k: "target_customers", label: "Target customers", ph: "e.g. Urban 18-35" },
  { k: "main_segment", label: "Main customer segment", ph: "e.g. Repeat online buyers 22-32" },
  { k: "typical_pricing", label: "Typical pricing", ph: "e.g. Flagship at ₹999" },
  { k: "sales_channels", label: "Main sales channels", ph: "e.g. Website, Amazon, Nykaa" },
  { k: "revenue_growth", label: "Revenue / growth (optional)", ph: "e.g. ₹6Cr ARR, 70% YoY" },
  { k: "business_goal", label: "Current business goal", ph: "e.g. Protect margin without losing repeat customers", area: true },
  { k: "website", label: "Website (optional)", ph: "https://…" },
];

const EMPTY = FIELDS.reduce((a, f) => ({ ...a, [f.k]: "" }), {});

export default function BusinessSetup() {
  const { business, setBusiness } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (business) setForm({ ...EMPTY, ...business });
  }, [business]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put("/business", form);
      setBusiness(data);
      if (form.website && form.website.trim()) {
        try {
          await api.post("/business/research");
          toast.success("Profile saved — researching your digital footprint");
          navigate("/intel");
          return;
        } catch (err) { console.error("Auto-research failed to start", err); }
      }
      toast.success("Business profile saved");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
            <Building2 className="w-4.5 h-4.5 text-white" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
            {business ? "Edit business profile" : "Set up your business profile"}
          </h1>
        </div>
        <p className="text-slate-500 mb-8 ml-12">This becomes the persistent context for every decision you analyze.</p>

        <form onSubmit={save} data-testid="business-form" className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.k} className={f.area ? "sm:col-span-2" : ""}>
                <Label className="text-sm text-slate-700">{f.label}{f.req && <span className="text-rose-500"> *</span>}</Label>
                {f.area ? (
                  <Textarea data-testid={`field-${f.k}`} value={form[f.k]} onChange={upd(f.k)} required={f.req}
                    className="mt-1.5 rounded-lg" placeholder={f.ph} rows={2} />
                ) : (
                  <Input data-testid={`field-${f.k}`} value={form[f.k]} onChange={upd(f.k)} required={f.req}
                    className="mt-1.5 rounded-lg" placeholder={f.ph} />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" disabled={loading} data-testid="save-business-button"
              className="bg-slate-900 hover:bg-slate-800 rounded-lg h-11 px-6">
              {loading ? "Saving…" : "Save profile"}
            </Button>
            {business && (
              <Button type="button" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-lg h-11">Cancel</Button>
            )}
          </div>
        </form>
      </div>
    </AppShell>
  );
}
